import { EntityQuery, Query, is, LegalPerson, LegalForm } from "@the-real-insight/tri-model";
import { userAPI } from "../UserAPI";
import { useEffect, useId, useState } from "react";

import { useTranslation } from "react-i18next";
import { Plus, Search, X } from "lucide-react";
import AddressEntry from "./AddressEntry";
import { useAdministrationAPI } from "../AdministrationAPI";
import { legalPersonAddressFields, legalPersonCompanyName } from "../utilities/legalPersonDisplay";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

/** Escape user input so it is matched literally inside a RegExp (prefix / starts-with search). */
function escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function formatLegalPersonMenuItem(lp: LegalPerson | any): string {
    const name = legalPersonCompanyName(lp);
    const id = lp?.tradeRegisterId;
    const city = lp?.tradeRegisterCity;
    const extras = [id, city].filter((x) => x != null && String(x).length > 0);
    if (extras.length === 0) {
        return name;
    }
    return `${name} (${extras.join(", ")})`;
}

interface Properties {
    className?: string;
    setLegalPerson?: (legalPerson: any) => void
}

const emptyAddressState = () => ({
    companyName: "",
    street: "",
    streetNumber: "",
    postalCode: "",
    city: "",
    country: "DE" as string,
});

/** Pflichtfeld-Stern wie in den übrigen Formularen. */
const RequiredMark = () => <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>;

export default function LegalPersonPicker(properties: Properties) {
    const { t } = useTranslation();
    const fieldId = useId();
    const { createLegalPerson } = useAdministrationAPI();
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [legalPersons, setLegalPersons] = useState([]) as any;
    const [legalPersonFilter, setLegalPersonFilter] = useState() as any;
    const [menuOpen, setMenuOpen] = useState(false) as any;
    const [createLegalPersonDialogOpen, setCreateLegalPersonDialogOpen] = useState(false) as any;
    const [editLegalPerson, setEditLegalPerson] = useState() as any;

    useEffect(() => {
        if (filter && filter.trim().length > 3) {
            setLoading(true);
        }

        const debounce = setTimeout(async () => {
            if (filter && filter.trim().length > 2) {
                try {
                    const prefix = escapeRegExp(filter.trim());
                    const pattern = `(?i)^${prefix}`;
                    const query = new Query(new EntityQuery(LegalPerson.type, [], undefined,
                        is('companyName').equalRegExp(pattern)), []);

                    const persons = await userAPI.query(query);

                    setLegalPersons(persons);
                    setMenuOpen(true);
                } catch (error) {
                    console.error('[LegalPersonPicker] query failed', error);
                } finally {
                    setLoading(false);
                }
            }
        }, 1500);

        return () => clearTimeout(debounce)
    }, [filter])

    const filterLegalPersons = async (filter: string) => {
        setFilter(filter);
        setLegalPersonFilter(null);
        setLegalPersons([]);

        if (properties.setLegalPerson) {
            properties.setLegalPerson(null);
        }
    };

    const pickLegalPerson = (legalPerson: LegalPerson) => {
        setLegalPersonFilter(legalPerson);
        setMenuOpen(false);

        if (properties.setLegalPerson) {
            properties.setLegalPerson(legalPerson);
        }
    };

    const isValid = () => {
        if (!editLegalPerson) {
            return false;
        }
        const cn = editLegalPerson.companyName?.trim?.() ?? "";
        const a = legalPersonAddressFields(editLegalPerson);
        return (
            cn.length > 0 &&
            !!a.street?.length &&
            !!a.streetNumber?.length &&
            !!a.postalCode?.length &&
            !!a.city?.length
        );
    };

    const addressForEntry = editLegalPerson
        ? legalPersonAddressFields(editLegalPerson)
        : { street: "", streetNumber: "", postalCode: "", city: "", country: "DE" };

    const fieldValue = legalPersonFilter ? legalPersonCompanyName(legalPersonFilter) : filter;

    return <><div className={properties.className || ''}>
        {/* Vorschlagsliste bewusst inline statt im Portal: in einem modalen Dialog
            wäre portalierter Inhalt unerreichbar (siehe AddressPicker). */}
        <div className="relative min-w-0 sm:max-w-[450px]">
            <Search aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
                id={fieldId}
                autoFocus
                type="search"
                className="px-8"
                placeholder={t('legalPersonPicker.enterNameOrTradeRegisterId')}
                value={fieldValue}
                onChange={(event) => filterLegalPersons(event.target.value)}
            />
            {fieldValue
                ?
                <button
                    type="button"
                    aria-label={t('global.clear')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => filterLegalPersons('')}
                >
                    <X className="h-4 w-4" />
                </button>
                :
                null}
            {menuOpen && legalPersons.length > 0
                ?
                <div className="absolute left-0 top-full z-[1000] mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                    {legalPersons.map((legalPerson: LegalPerson) => {
                        const label = formatLegalPersonMenuItem(legalPerson);
                        return (
                            <button
                                type="button"
                                key={`${(legalPerson as any)._id ?? label}`}
                                className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                                onMouseDown={(event) => {
                                    // mousedown (not click) so the search field's blur cannot interfere
                                    event.preventDefault();
                                    pickLegalPerson(legalPerson);
                                }}
                                onKeyDown={(event) => {
                                    // Enter/Space only — the mouse path never reaches click, so nothing fires twice.
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        pickLegalPerson(legalPerson);
                                    }
                                }}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
                :
                null}
        </div>
        {loading
            ?
            <div className="mt-3 text-sm text-muted-foreground">{t('global.loading')}...</div>
            :
            (legalPersonFilter
                ?
                <div className="mt-3 text-sm font-medium">{formatLegalPersonMenuItem(legalPersonFilter)}</div>
                :
                <Button variant="ghost" type="button" className="mt-1 gap-2" onClick={() => {
                    setEditLegalPerson(emptyAddressState());
                    setCreateLegalPersonDialogOpen(true);
                }}>
                    <Plus className="h-4 w-4" aria-hidden />
                    {t('legalPersonPicker.companyNotFound')}?
                </Button>)
        }
    </div>
        {editLegalPerson ?
            <Dialog
                open={createLegalPersonDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setCreateLegalPersonDialogOpen(false);
                    }
                }}
            >
                <DialogContent
                    className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[700px]"
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{t('legalPersonPicker.createCompany')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-name`}>{t('legalPersonPicker.name')}<RequiredMark /></Label>
                            <Input id={`${fieldId}-name`} value={editLegalPerson.companyName ?? ""} onChange={(event) => {
                                setEditLegalPerson({ ...editLegalPerson, companyName: event.target.value });
                            }} />
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <div className="flex w-32 shrink-0 flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-tradeRegisterId`}>{t('legalPersonPicker.tradeRegisterId')}<RequiredMark /></Label>
                                <Input id={`${fieldId}-tradeRegisterId`} placeholder="HRB 1234" value={editLegalPerson.tradeRegisterId ?? ""} onChange={(event) => {
                                    setEditLegalPerson({ ...editLegalPerson, tradeRegisterId: event.target.value });
                                }} />
                            </div>
                            <div className="flex min-w-0 flex-1 basis-56 flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-tradeRegisterCity`}>{t('legalPersonPicker.tradeRegisterCity')}<RequiredMark /></Label>
                                <Input id={`${fieldId}-tradeRegisterCity`} value={editLegalPerson.tradeRegisterCity ?? ""} onChange={(event) => {
                                    setEditLegalPerson({ ...editLegalPerson, tradeRegisterCity: event.target.value });
                                }} />
                            </div>
                            <div className="flex w-48 shrink-0 flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-legalForm`}>{t('legalPersonPicker.legalForm')}</Label>
                                <Select
                                    defaultValue="GmbH"
                                    value={editLegalPerson.legalForm}
                                    onValueChange={(legalForm) => {
                                        setEditLegalPerson({ ...editLegalPerson, legalForm });
                                    }}
                                >
                                    <SelectTrigger id={`${fieldId}-legalForm`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {Object.keys(LegalForm).map((key: any) => (
                                            <SelectItem key={key} value={key}>{key}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="text-lg font-semibold">{t('legalPersonPicker.address')}</div>
                        <AddressEntry address={addressForEntry} addressChanged={(addr: any) => {
                            setEditLegalPerson({
                                ...editLegalPerson,
                                street: addr.street,
                                streetNumber: addr.streetNumber,
                                postalCode: addr.postalCode,
                                city: addr.city,
                                country: addr.country,
                            });
                        }}></AddressEntry>
                        <div className="flex flex-wrap gap-4">
                            <div className="flex w-40 shrink-0 flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-taxID`}>{t('legalPersonPicker.taxID')}<RequiredMark /></Label>
                                <Input id={`${fieldId}-taxID`} placeholder={`${t('global.eg')} DE124356789`} value={editLegalPerson.taxID ?? ""} onChange={(event) => {
                                    setEditLegalPerson({ ...editLegalPerson, taxID: event.target.value });
                                }} />
                            </div>
                            <div className="flex min-w-0 flex-1 basis-56 flex-col gap-1.5">
                                <Label htmlFor={`${fieldId}-iban`}>{t('legalPersonPicker.iban')}<RequiredMark /></Label>
                                <Input id={`${fieldId}-iban`} placeholder={`${t('global.eg')} DE02600501010002034304`} value={editLegalPerson.iban ?? ""} onChange={(event) => {
                                    setEditLegalPerson({ ...editLegalPerson, iban: event.target.value });
                                }} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4 sm:gap-0">
                        <Button variant="outline" type="button" onClick={() => {
                            setCreateLegalPersonDialogOpen(false);
                        }}>
                            {t('global.cancel')}
                        </Button>
                        <Button type="button" disabled={!isValid()} onClick={async () => {
                            const newLegalPerson = await createLegalPerson(editLegalPerson);

                            if (properties.setLegalPerson) {
                                properties.setLegalPerson(newLegalPerson);
                            }

                            setCreateLegalPersonDialogOpen(false);
                        }}>
                            {t('global.add')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            :
            <></>
        }
    </>
}
